import React from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import Header from "../components/site/Header";
import Seo from "../components/site/Seo";
import Footer from "../components/site/Footer";
import CTAButton from "../components/site/CTAButton";
import Photo from "../components/site/Photo";
import ScrollReveal from "../components/site/ScrollReveal";
import { SisterServices, Breadcrumb, FinalCTA } from "../components/site/ServiceSections";

export default function About() {
  return (
    <div className="min-h-screen bg-ink text-bone">
      <Seo />
      <Header />
      <Hero />
      <Manifesto />
      <HowWeWork />
      <Numbers />
      <PhotoWall />
      <SisterServices keys={["menu", "food", "venue", "brand"]} />
      <FinalCTA
        eyebrow="Final step · Hand-confirmed quotes"
        headline="Want to work"
        italic="together?"
        intro="Quick form below or call directly. We pick up the phone — no auto-replies, no marketing chase, no agency middlemen."
        backdropSlug="hero-banquet-01"
        formTitle="Tell us about your shoot."
      />
      <Footer />
    </div>
  );
}

const Hero = () => (
  <section className="relative min-h-[100svh] overflow-hidden bg-ink">
    <div className="absolute inset-0">
      <Photo slug="venue-buffet-01" alt="About Restaurant Photographer Melbourne — behind the lens" priority ratio="auto" className="!relative h-full w-full" />
    </div>
    <div className="absolute inset-0 bg-gradient-to-b from-ink/80 via-ink/50 to-ink" />
    <div className="absolute inset-0 bg-gradient-to-r from-ink/85 via-ink/35 to-transparent" />

    <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10 min-h-[100svh] flex flex-col pt-24 sm:pt-28 lg:pt-32 pb-10 lg:pb-12">
      <Breadcrumb current="About" />

      <div className="flex-1 flex flex-col justify-center max-w-5xl">
        <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-5 sm:mb-7 font-bold">
          About · Hospitality photography from inside the kitchen
        </p>

        <h1 className="font-display tracking-editorial leading-[0.92]" style={{ fontSize: "clamp(2.5rem, 7.5vw, 6.5rem)" }}>
          We shoot food
          <br />
          like we&apos;ve <span className="italic font-light text-ember">worked the pass.</span>
        </h1>

        <p className="mt-6 sm:mt-8 max-w-2xl text-base sm:text-lg md:text-xl leading-relaxed text-bone/85">
          A Melbourne photography studio working only with hospitality — restaurants,
          cafés, bars, hotels and food brands. Built around the rhythm of a kitchen:
          arrive early, light fast, plate fresh, don&apos;t hold up service.
        </p>

        <div className="mt-8 sm:mt-10 flex flex-wrap gap-3">
          <Link to="/get-a-quote" className="w-full sm:w-auto">
            <CTAButton variant="primary" size="lg" className="w-full sm:w-auto">Request A Quote</CTAButton>
          </Link>
          <a href="tel:0494756111" className="w-full sm:w-auto">
            <CTAButton variant="ghost" size="lg" arrow={false} className="w-full sm:w-auto">Call 0494 756 111</CTAButton>
          </a>
        </div>
      </div>
    </div>
  </section>
);

const BELIEFS = [
  { n: "01", title: "Real plating, every time", desc: "Your kitchen plates the food. We don't ship dishes to studios, swap sauces for motor oil, or style with fake garnish. The plate that gets photographed is the plate a customer would receive." },
  { n: "02", title: "Service comes first", desc: "Shoots run around service — not the other way around. We arrive before doors, we don't tie up kitchen leads, we don't hold pass plates. If a venue's about to open, we step aside." },
  { n: "03", title: "Melbourne is the room", desc: "We work only in Greater Melbourne and we know the suburbs. Tarneit's family kitchens, Glen Waverley's Asian dining strip, Brunswick wine bars, the Footscray morning trade. Local context shapes how we shoot." },
  { n: "04", title: "Quote it. Invoice it.", desc: "No retouching surprises, no per-channel licence top-ups, no agency middlemen. The price you're quoted is the price you're invoiced — confirmed in writing the same day you call." },
];

const Manifesto = () => (
  <section className="relative bg-ink text-bone py-24 lg:py-36 overflow-hidden">
    <div className="absolute inset-0 grain opacity-50 pointer-events-none" />
    <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10">
      <div className="grid grid-cols-12 gap-8 lg:gap-16 mb-14 lg:mb-20 items-end">
        <div className="col-span-12 lg:col-span-7">
          <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-6 font-bold">
            What we believe · Hospitality first
          </p>
          <h2 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2.25rem, 5.5vw, 4.75rem)" }}>
            Photography is
            <br />
            <span className="italic font-light text-ember">an operational asset.</span>
          </h2>
        </div>
        <div className="col-span-12 lg:col-span-5 lg:pb-3">
          <p className="text-bone/80 text-base md:text-lg leading-relaxed">
            Most hospitality photography is dressed up as art when it should be
            built as infrastructure. Photos run delivery menus, Google profiles,
            and Saturday night bookings. We treat them that way.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-bone/10 border border-bone/10">
        {BELIEFS.map((b) => (
          <ScrollReveal key={b.n}>
            <div className="bg-ink hover:bg-[#161613] transition-colors p-8 md:p-10 h-full">
              <span className="font-display italic text-2xl text-ember leading-none">{b.n}</span>
              <h3 className="font-display text-2xl md:text-3xl tracking-editorial leading-tight mt-3">{b.title}</h3>
              <p className="mt-5 text-base text-bone/75 leading-relaxed max-w-md">{b.desc}</p>
            </div>
          </ScrollReveal>
        ))}
      </div>
    </div>
  </section>
);

const HowWeWork = () => (
  <section className="bg-bone text-ink py-24 lg:py-36">
    <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
      <div className="grid grid-cols-12 gap-8 lg:gap-16">
        <div className="col-span-12 lg:col-span-5">
          <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-6 font-bold">
            How we work · From brief to delivery
          </p>
          <h2 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2.25rem, 5.5vw, 4.5rem)" }}>
            One photographer.
            <br />
            <span className="italic font-light text-ember">One library.</span>
          </h2>
          <p className="mt-8 text-char text-base md:text-lg leading-relaxed max-w-md">
            We don&apos;t sub-contract. The photographer who answers your first call
            is the same person standing in your kitchen, lighting your plates and
            editing your files. That continuity is the whole point.
          </p>
          <Link to="/packages" className="inline-block mt-10">
            <CTAButton variant="secondary" size="md">See the packages</CTAButton>
          </Link>
        </div>

        <div className="col-span-12 lg:col-span-7">
          <ul className="space-y-7">
            {[
              ["Working venues, not studios", "Every shoot happens on your floor — your kitchen, your tables, your light. The room is part of the photograph."],
              ["Multi-cultural Melbourne fluency", "We work routinely with Indian, Sri Lankan, Vietnamese, Italian, Greek, Lebanese, Chinese, Korean and Japanese kitchens. Plating, ingredients and dish naming travel with us."],
              ["Channel-spec from frame one", "Crops are planned for the channels you actually use — Uber Eats square, Google 4:3, Instagram vertical, print 16:10. One frame, every output."],
              ["Honest about what won't work", "If a dish, light situation or scope won't deliver value, we'll say so on the call. Better to flag it than overdeliver a photograph nobody uses."],
            ].map(([title, desc]) => (
              <li key={title} className="flex items-start gap-5 pb-7 border-b border-ink/15 last:border-0">
                <Check size={18} strokeWidth={2} className="text-ember mt-1 shrink-0" />
                <div>
                  <h3 className="font-display text-xl md:text-2xl tracking-editorial leading-tight">{title}</h3>
                  <p className="mt-3 text-sm md:text-base text-char leading-relaxed max-w-xl">{desc}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  </section>
);

const Numbers = () => (
  <section className="bg-ink text-bone py-24 lg:py-36">
    <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
      <div className="grid grid-cols-12 gap-8 mb-14 lg:mb-16 items-end">
        <div className="col-span-12 lg:col-span-7">
          <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-6 font-bold">
            By the numbers · Where we work
          </p>
          <h2 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2.25rem, 5.5vw, 4.5rem)" }}>
            Built for the volume
            <br />
            <span className="italic font-light text-ember">Melbourne actually runs.</span>
          </h2>
        </div>
        <div className="col-span-12 lg:col-span-5 lg:pb-3">
          <p className="text-bone/75 text-base md:text-lg leading-relaxed max-w-md">
            Hospitality photography that scales — from a 15-dish café refresh up to
            a 100-dish full-menu library or a multi-venue franchise rollout.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-bone/15 border border-bone/15">
        {[
          ["100+", "Dishes photographable in a single day"],
          ["5–7 days", "Standard turnaround on edited files"],
          ["All of Melbourne", "No travel fees inside the metro"],
          ["$10/image", "Volume-tier rate at 100+ images"],
        ].map(([n, c]) => (
          <div key={c} className="bg-ink p-7 md:p-10">
            <p className="font-sans font-bold tracking-tight leading-none" style={{ fontSize: "clamp(1.75rem, 3vw, 2.75rem)" }}>{n}</p>
            <p className="mt-4 text-sm md:text-base text-bone/75 leading-relaxed">{c}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const WALL = [
  "hero-banquet-01", "menu-malai-tikka", "hero-spread-01",
  "menu-butter-chicken", "hero-laddu-220", "hero-gulab-jamun-00",
  "menu-falooda", "menu-veg-crispy", "menu-chinese-bhel-03",
];

const PhotoWall = () => (
  <section className="bg-bone py-24 lg:py-36">
    <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
      <p className="text-xs uppercase tracking-wider2 text-ember mb-6 font-bold">
        From the library · Recent work
      </p>
      <h2 className="font-display text-ink tracking-editorial leading-[0.95] mb-14" style={{ fontSize: "clamp(2.25rem, 5vw, 4rem)" }}>
        Plates, rooms, moments —
        <br />
        <span className="italic font-light text-ember">all on location.</span>
      </h2>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-5">
        {WALL.map((s, i) => (
          <Photo
            key={s + i}
            slug={s}
            alt={`Restaurant Photographer Melbourne — ${s}`}
            ratio={i % 3 === 0 ? "4/5" : "1/1"}
            sizes="(min-width: 768px) 30vw, 50vw"
          />
        ))}
      </div>
    </div>
  </section>
);

// Unused import guard
